import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <div className="border-b border-[#1A1A1A]">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <Link to="/settings" className="inline-flex items-center gap-2 text-muted-foreground hover:text-white transition-colors mb-4">
            <ArrowLeft size={16} />
            Back to Settings
          </Link>
          <div className="flex items-center gap-3">
            <Shield size={28} className="text-gold" />
            <h1 className="font-serif text-3xl text-white">Terms of Service</h1>
          </div>
          <p className="text-muted-foreground mt-2">Last updated: March 2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="prose prose-invert prose-gold max-w-none space-y-8">
          
          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">1. Acceptance of Terms</h2>
            <p className="text-gray-400 leading-relaxed">
              By accessing and using Assagart ("the Platform"), you accept and agree to be bound by these Terms of Service. 
              If you do not agree to these terms, please do not use our services. These terms apply to all visitors, users, 
              and others who access or use the Platform.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">2. Description of Service</h2>
            <p className="text-gray-400 leading-relaxed">
              Assagart is a luxury asset management platform that provides tools for tracking, managing, and analysing 
              high-value collectibles and assets including but not limited to fine art, luxury cars, fine wine and spirits, 
              fine wine, jewellery, watches, and designer handbags. Our services include portfolio tracking, AI-powered 
              valuations, market insights, and curated investment opportunities.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">3. User Accounts</h2>
            <p className="text-gray-400 leading-relaxed">
              To access certain features of the Platform, you must register for an account. You agree to provide accurate, 
              current, and complete information during registration and to update such information to keep it accurate, 
              current, and complete. You are responsible for safeguarding your password and for all activities that occur 
              under your account. You agree to notify us immediately of any unauthorised use of your account.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">4. Subscription and Payments</h2>
            <p className="text-gray-400 leading-relaxed">
              Assagart offers various subscription tiers (Starter, Professional, and Elite) with different features and pricing. 
              By subscribing to a paid plan, you agree to pay all applicable fees. Subscription fees are billed in advance on a 
              monthly or annual basis. All payments are non-refundable except as required by law or as explicitly stated in these terms.
            </p>
            <ul className="text-gray-400 list-disc list-inside space-y-2 mt-4">
              <li>Starter: Free tier with limited features</li>
              <li>Professional: £29/month or £290/year</li>
              <li>Elite: £79/month or £790/year</li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">5. AI Valuations Disclaimer</h2>
            <p className="text-gray-400 leading-relaxed">
              The AI-powered valuations provided by Assagart are estimates based on market data, comparable sales, and algorithmic 
              analysis. These valuations are for informational purposes only and should not be considered as professional appraisals 
              or financial advice. Actual market values may vary significantly. We recommend obtaining professional appraisals for 
              insurance, sale, or legal purposes.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">6. Market Opportunities</h2>
            <p className="text-gray-400 leading-relaxed">
              Curated market opportunities displayed on the Platform are sourced from third-party auction houses, dealers, and 
              brokers. Assagart does not guarantee the availability, authenticity, or accuracy of these listings. We are not 
              party to any transactions between users and third-party sellers. Users should conduct their own due diligence 
              before making any purchase decisions.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">7. Intellectual Property</h2>
            <p className="text-gray-400 leading-relaxed">
              The Platform and its original content, features, and functionality are owned by Assagart and are protected by 
              international copyright, trademark, patent, trade secret, and other intellectual property laws. You may not 
              reproduce, distribute, modify, or create derivative works of any part of the Platform without our express 
              written permission.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">8. User Content</h2>
            <p className="text-gray-400 leading-relaxed">
              You retain ownership of any content you submit to the Platform, including asset information, images, and documents. 
              By submitting content, you grant Assagart a non-exclusive, worldwide, royalty-free licence to use, store, and 
              process such content solely for the purpose of providing our services to you.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">9. Privacy and Data Protection</h2>
            <p className="text-gray-400 leading-relaxed">
              Your privacy is important to us. Our collection and use of personal information is governed by our Privacy Policy, 
              which is incorporated into these Terms by reference. By using the Platform, you consent to the collection and use 
              of your information as described in our Privacy Policy.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">10. Limitation of Liability</h2>
            <p className="text-gray-400 leading-relaxed">
              To the maximum extent permitted by law, Assagart shall not be liable for any indirect, incidental, special, 
              consequential, or punitive damages, including but not limited to loss of profits, data, or other intangible 
              losses, resulting from your use of or inability to use the Platform, any conduct or content of any third party 
              on the Platform, or unauthorised access to your account.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">11. Indemnification</h2>
            <p className="text-gray-400 leading-relaxed">
              You agree to indemnify and hold harmless Assagart, its officers, directors, employees, and agents from any claims, 
              damages, losses, liabilities, and expenses (including legal fees) arising out of or related to your use of the 
              Platform, violation of these Terms, or infringement of any third-party rights.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">12. Modifications to Terms</h2>
            <p className="text-gray-400 leading-relaxed">
              We reserve the right to modify these Terms at any time. We will notify users of any material changes by posting 
              the updated Terms on the Platform and updating the "Last updated" date. Your continued use of the Platform after 
              such modifications constitutes your acceptance of the updated Terms.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">13. Governing Law</h2>
            <p className="text-gray-400 leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of England and Wales, without regard 
              to its conflict of law provisions. Any disputes arising under these Terms shall be subject to the exclusive 
              jurisdiction of the courts of England and Wales.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">14. Contact Information</h2>
            <p className="text-gray-400 leading-relaxed">
              If you have any questions about these Terms of Service, please contact us at:
            </p>
            <div className="mt-4 p-4 bg-[#111111] border border-[#262626]">
              <p className="text-white">Assagart Limited</p>
              <p className="text-gray-400">Email: assagart.contact@gmail.com</p>
              <p className="text-gray-400">Address: London, United Kingdom</p>
            </div>
          </section>

        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-[#1A1A1A] py-8">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-muted-foreground text-sm">© 2026 Assagart. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
